import React, { useCallback, useEffect, useState } from 'react';
import { trace } from 'firebase/performance';
import Navbar from "../../components/admin/Navbar.tsx";
import { Outlet, useLocation, useParams } from "react-router-dom";
import TableUser from "../../components/admin/user/TableUser.tsx";
import OptionsUser from "../../components/admin/user/OptionsUser.tsx";
import { Users } from '../../models/User.tsx';
import { useUser } from '../../context/UserContext.tsx';
import {
  invalidateVisitedUser,
  loadVisitedUserProfile,
  peekVisitedUser,
  peekVisitedUserProfile,
  seedVisitedUserPreview,
  VisitedUserNavState,
} from '../../helpers/adminVisitedUserCache.tsx';
import { subscribeCache } from '../../helpers/queryCache.tsx';
import { showUserbets } from '../../services/admin/BetsService.tsx';
import { isFreePlayBet } from '../../helpers/functions.tsx';
import { perf } from '../../services/Firebase.js';

const visitedUserProfileKey = (userName: string) => `adminVisitedUser:${userName}`;

export type VisitedUserOutletContext = {
  refetchUser: () => void;
  refresh: number;
  visitedUser: Users | null;
  isVisitedUserLoading: boolean;
};

export const LayoutUserAdmin = () => {

  const [refresh, setRefresh] = useState(0);
  const { user } = useParams<{ user: string }>();
  const location = useLocation();
  const { setPendingBet } = useUser();
  const [visitedUser, setVisitedUser] = useState<Users | null>(null);
  const [isVisitedUserLoading, setIsVisitedUserLoading] = useState(!!user);

  const refetchUser = useCallback(() => {
    if (user) invalidateVisitedUser(user);
    setRefresh(prev => prev + 1);
  }, [user]);

  useEffect(() => {
    document.body.classList.add("admin-body");
    return () => {
      document.body.classList.remove("admin-body");
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setVisitedUser(null);
      setIsVisitedUserLoading(false);
      setPendingBet(null);
      return;
    }

    let cancelled = false;
    const state = (location.state ?? {}) as VisitedUserNavState;

    if (state.visitedUserPreview) {
      seedVisitedUserPreview(user, state.visitedUserPreview);
    }

    const previewForId = state.visitedUserPreview ?? peekVisitedUser(user);
    const userId = state.userId ?? previewForId?.id;
    let profileSettled = false;
    let pendingSettled = false;
    let screenTraceStopped = false;

    const screenTrace = trace(perf, 'loadVisitedUser');
    screenTrace.putAttribute('user_name', user.slice(0, 100));
    screenTrace.start();

    const syncLoading = () => {
      if (cancelled) return;
      // Pending bets must NOT block the user shell — only the profile does.
      const stillLoading = !profileSettled;
      setIsVisitedUserLoading(stillLoading);

      if (profileSettled && pendingSettled && !screenTraceStopped) {
        screenTraceStopped = true;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!cancelled) screenTrace.stop();
          });
        });
      }
    };

    setPendingBet(null);
    setVisitedUser(null);
    setIsVisitedUserLoading(true);
    const unsubProfile = subscribeCache<Users>(visitedUserProfileKey(user), (userData) => {
      if (!cancelled && userData.userName?.toLowerCase() === user.toLowerCase()) {
        setVisitedUser(userData);
      }
    });

    const loadProfile = async () => {
      try {
        const userData = await loadVisitedUserProfile(user, true, userId);
        if (!cancelled) setVisitedUser(userData);
      } catch (error) {
        console.error('Error loading visited user:', error);
        if (!cancelled) {
          const stale = peekVisitedUserProfile(user);
          if (stale) setVisitedUser(stale);
        }
      } finally {
        if (!cancelled) {
          profileSettled = true;
          syncLoading();
        }
      }
    };

    const loadPending = async () => {
      const t = trace(perf, 'loadVisitedUserPending');
      t.putAttribute('user_name', user.slice(0, 100));
      t.start();
      try {
        const bets = await showUserbets(user);
        if (!cancelled) {
          const headerBets = bets.filter(bet => !isFreePlayBet(bet));
          t.putMetric('bet_count', headerBets.length);
          setPendingBet(headerBets);
        }
      } catch (error) {
        console.error('Error loading pending header:', error);
        if (!cancelled) setPendingBet([]);
      } finally {
        t.stop();
        if (!cancelled) {
          pendingSettled = true;
          syncLoading();
        }
      }
    };

    loadProfile();
    loadPending();

    return () => {
      cancelled = true;
      if (!screenTraceStopped) {
        screenTraceStopped = true;
        screenTrace.putAttribute('cancelled', 'true');
        screenTrace.stop();
      }
      unsubProfile();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refresh, setPendingBet]);

  const outletContext: VisitedUserOutletContext = {
    refetchUser,
    refresh,
    visitedUser,
    isVisitedUserLoading,
  };

  return (
    <>
      <Navbar />
      <div className="grow container-padding admin-shell">
        <div className="col-12">
          <TableUser
            visitedUser={visitedUser}
            isVisitedUserLoading={isVisitedUserLoading}
            onUserUpdated={refetchUser}
          />
        </div>
        <div className="col-12">
          <OptionsUser visitedUserName={user} />
        </div>
        <div className='justify-content-center'>
          <div className='col-md-12 col-xs-12'>
            <Outlet context={outletContext} />
          </div>
        </div>
      </div>
    </>
  );
};
